'use client';

import { ComponentProps } from 'react';

import { cx } from '../cva';
import { useField } from './context';

export type FieldTextAreaProps = ComponentProps<'textarea'> & {
    hasError?: boolean;
};

export function FieldTextArea({
    className,
    disabled,
    hasError: hasErrorProp,
    required: requiredProp,
    onChange,
    ...props
}: FieldTextAreaProps) {
    const { id, error, name, required } = useField();

    let ariaDescription: string | undefined;

    if (error) {
        ariaDescription = `${id}-error`;
    }

    const hasError = Boolean(error);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        if (!disabled && onChange) {
            onChange(e);
        }
    };

    return (
        <textarea
            className={cx(
                'px-sm py-xs flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-white outline-none transition',
                className,
                'focus-visible:outline-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                !hasError && 'hover:border-gray-200',
                !hasError && 'focus:border-gray-500',
                hasError && 'border-error bg-error/10'
            )}
            data-error={hasError}
            id={id}
            name={name}
            aria-describedby={ariaDescription}
            aria-invalid={hasError || hasErrorProp}
            aria-disabled={disabled}
            disabled={disabled}
            data-disabled={disabled ? '' : undefined}
            aria-required={required || requiredProp}
            onChange={handleChange}
            {...props}
        />
    );
}
